/**
 * THE SERVICE ADVISOR'S WALK — the desk half of a job, in order.
 *
 * Anthony: "Work on wizard track for Service Advisor."
 *
 * The technician's walk starts when somebody is standing at the car.
 * Everything before that, and the money after it, is this. Who they are and
 * what the car is doing, a price, sending it, getting the yes, booking it,
 * putting somebody on it, the bill and the payment.
 *
 * ─── IT SHARES ITS RULES, NOT ITS CODE ─────────────────────────────────
 *
 * What counts as finished lives in domain/tracks.ts, which both walks and
 * both entry cards read. That is deliberate: the moment "is this ready to
 * send" is decided in two places, the card and the walk start disagreeing
 * and the person in front of them stops trusting either.
 *
 * ─── AND IT DOES THE WORK HERE ─────────────────────────────────────────
 *
 * Anthony: "take you to a panel to start filling it all out." Where a step
 * can be finished in one field it is finished here -- the yes, the
 * booking, the crew, the invoice, the payment. Where it needs the full
 * editor, the step says what is missing and opens the right tab of the job
 * rather than pretending a wizard can replace a line editor.
 */

import { isIdentified } from "../domain/trade.ts";
import { currentTrade } from "../ui/words.ts";
import { getJob, listLines, setScheduledAt, meAsTechnician, listAuthorizations,
  setJobTechnicians, jobTechnicians, type JobRow, type LineItem } from "../data/jobs.ts";
import { moneyFor, raiseInvoice, recordPayment, METHOD_LABEL, type Method } from "../data/payments.ts";
import { sendTheQuote, canEmailQuote } from "../data/sendquote.ts";
import { authForm } from "./authorizations.ts";
import { esc, money, mount } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";
import { ADVISOR_STEPS, nextOn, leftOn, openOn, reword, type JobFacts, type Step } from "../domain/tracks.ts";
import { shopWords } from "../ui/words.ts";
import { isVisitOnly } from "../domain/visitquote.ts";
import { shopStamp, shopWallValue } from "../ui/shopclock.ts";

/** The stages the rail draws, and which steps sit under each. */
const STAGES: Array<{ name: string; keys: string[] }> = [
  { name: "Take it", keys: ["who"] },
  { name: "Price it", keys: ["price", "send"] },
  { name: "Agree it", keys: ["yes"] },
  { name: "Book it", keys: ["book", "who_goes"] },
  { name: "Close it", keys: ["bill", "money"] },
];

/**
 * WHAT IS STILL OWED, AND BY WHICH SIDE.
 *
 * Anthony, 2026-09-15: "if a step in the process isn't finished, put it in
 * the wizard to let one side or the other know it needs done."
 *
 * The technician's walk has said this since it was built — "Still owed:
 * photos from before you started, a plate or a VIN". The advisor's walk
 * never has. It shows the step you are on and nothing about the rest, so
 * the desk's own outstanding work is only visible by walking to the end of
 * the wizard to find out.
 *
 * Job 1747 is what that costs. Matthew Villa approved $450 at 1:32pm. The
 * job then needed a technician to confirm and a date, and nothing on the
 * advisor's screen ever said so. It sat, and at 4:20pm it was cancelled.
 *
 * BOTH SIDES, because the sentence says both. `leftOn` already answers
 * this for either track and was imported into this file and never called;
 * the work was done and simply not shown. The desk seeing that the tech
 * still owes photos is the same fact as the tech seeing that the desk
 * still owes a price, and neither can chase what they cannot see.
 *
 * The step being worked on right now is left out. It is on the screen
 * already, in bigger letters, and repeating it in a list of what is
 * outstanding reads as though it were somebody else's problem.
 */
function owedHtml(facts: JobFacts, at: string): string {
  const mine = leftOn("advisor", facts, currentTrade()).filter((s) => s.key !== at);
  const theirs = leftOn("tech", facts, currentTrade());
  if (!mine.length && !theirs.length) return "";

  const line = (label: string, steps: Step[]): string =>
    steps.length
      ? `<span class="owed-line"><b>${esc(label)}</b> ${
          esc(steps.map((s) => s.title.toLowerCase()).join(", "))}</span>`
      : "";

  return `<div class="owed">
    ${line("Still on the desk:", mine)}
    ${line(`Waiting on the ${shopWords().techLabel}:`, theirs)}
  </div>`;
}

const stageOf = (k: string): number =>
  Math.max(0, STAGES.findIndex((g) => g.keys.includes(k)));
const stepAt = (k: string): number => Math.max(0, ADVISOR_STEPS.findIndex((s) => s.key === k));

/**
 * The job as the rules see it. One read, one shape, and the same facts the
 * entry card was built from, so the walk never opens somewhere the card did
 * not predict.
 */
export async function factsFor(job: JobRow, lines: LineItem[]): Promise<JobFacts> {
  const [auths, crew, m] = await Promise.all([
    listAuthorizations(job.id).catch(() => []),
    jobTechnicians(job.id).catch(() => []),
    moneyFor(job.id, 0).catch(() => null),
  ]);
  return factsFrom(job, lines, {
    live: auths.filter((a) => !a.declined).length,
    crew: crew.length,
    invoiced: Boolean(m?.invoiceId),
    paidAmount: Number(m?.paid ?? 0),
  });
}

/**
 * THE SAME FACTS, WITHOUT THE ROUND TRIPS.
 *
 * The job card has already fetched the lines, the authorizations, the crew
 * and the money before it draws a single pixel. Making it fetch them again
 * so the entry cards could be built would be four extra calls per job card
 * for numbers already sitting in scope, and worse, four chances for the
 * card and the walk to disagree because one of them read a moment later.
 */
export function factsFrom(job: JobRow, lines: LineItem[], have: {
  live: number; crew: number; invoiced: boolean; paidAmount: number;
  found?: boolean; arrivalShot?: boolean; releaseShot?: boolean;
}): JobFacts {
  const place = String(job.location ?? job.customers?.address ?? "").trim();

  return {
    status: job.status,
    hasCustomer: Boolean(job.customers?.full_name?.trim()),
    hasPhone: String(job.customers?.phone ?? "").replace(/\D/g, "").length >= 10,
    /**
     * WHERE THE CAR IS, only asked when it is a real question.
     *
     * Anthony: "If the job is marked In the Shop, shops can add their shop
     * address in their shop profile." A job that is not explicitly MOBILE
     * is at the shop, and the shop's address is written once in Settings.
     * Demanding a per-job address on all of them was the walk asking for
     * something the shop had already answered -- and service_at is null on
     * most jobs, so it was asking on almost all of them.
     */
    /* A case file asks for no address, and a city is enough when there is
       one (Anthony, 2026-10-14: "don't require address ... can just be a city"). */
    hasPlace: Boolean(shopWords().caseFile) || job.service_at !== "mobile" || place.length >= 5,
    hasCar: Boolean(job.vehicles?.make || job.vehicles?.description),
    hasComplaint: String(job.complaint ?? job.title ?? "").trim().length >= 4,
    priced: lines.reduce((n, l) => n + Number(l.total ?? 0), 0),
    quoteSent: Boolean(job.quote_expires_at),
    authorized: have.live > 0,
    scheduled: Boolean(job.scheduled_at),
    assigned: have.crew > 0,
    started: Boolean(job.status === "in_progress" || job.completed_at),
    arrivalShot: have.arrivalShot ?? false,
    releaseShot: have.releaseShot ?? false,
    found: have.found ?? false,
    identified: isIdentified(currentTrade(), job.vehicles),
    invoiced: have.invoiced,
    paid: job.status === "paid" || have.paidAmount > 0.005,
    noCharge: Boolean(job.no_charge_reason),
    /* A garage door shop's free estimate visit. See domain/visitquote.ts. */
    visitQuote: shopWords().quoteIsVisit && isVisitOnly(lines),
  };
}

export async function advisorPage(jobId: string, role: Role): Promise<void> {
  mount(`<div class="wrap"><div class="loading">Opening the job&hellip;</div></div>`);

  const job = await getJob(jobId);
  if (!job) { mount(`<div class="wrap"><div class="empty">That job is not here.</div></div>`); return; }

  let lines = await listLines(jobId).catch(() => [] as LineItem[]);
  let facts = await factsFor(job, lines);
  let at: string = openOn("advisor", facts, currentTrade())?.key ?? ADVISOR_STEPS[0]!.key;

  const reread = async (): Promise<void> => {
    const fresh = await getJob(jobId);
    if (fresh) Object.assign(job, fresh);
    lines = await listLines(jobId).catch(() => lines);
    facts = await factsFor(job, lines);
  };

  const go = async (k: string): Promise<void> => { at = k; await draw(); };
  const onward = async (): Promise<void> => {
    await reread();
    const next = nextOn("advisor", facts, currentTrade());
    if (!next) { at = "finished"; await draw(); return; }
    at = next.key;
    await draw();
  };

  const draw = async (): Promise<void> => {
    const car = [job.vehicles?.year, job.vehicles?.make, job.vehicles?.model]
      .filter(Boolean).join(" ").trim() || `the ${shopWords().thing}`;

    if (at === "finished") {
      mount(`<div class="wrap advwrap">
        <div class="card pad techpanel">
          <div class="techok"><b>Nothing left on the desk for this one.</b>
            <p>Everything the advisor owes is on the job. If work is still to be
            done, the technician's walk is where it lives.</p></div>
          <div class="techacts">
            <a class="btn primary wide" href="#/job/${esc(job.id)}">Back to the job</a>
            <a class="btn" href="#/jobs">The board</a>
          </div>
        </div></div>`);
      return;
    }

    const step = reword(ADVISOR_STEPS[stepAt(at)]!, currentTrade());
    const i = stepAt(at);

    mount(`
      <div class="wrap advwrap">
        <div class="techhead">
          <a class="techback" href="#/job/${esc(job.id)}">&larr; The whole job</a>
          <div class="techcar">${esc(job.job_number)} &middot; ${esc(car)}</div>
        </div>
        <div class="techrail">
          ${STAGES.map((g, n) => {
            const here = n === stageOf(at);
            const past = n < stageOf(at);
            return `<span class="techstage ${past ? "done" : here ? "on" : ""}">
              <i></i><b>${esc(g.name)}</b></span>`;
          }).join("")}
        </div>
        <div class="card pad techpanel">
          <div class="techstep">Step ${i + 1} of ${ADVISOR_STEPS.length}</div>
          <h2 class="techtitle">${esc(step.title)}</h2>
          <p class="techsub">${esc(step.sub)}</p>
          ${owedHtml(facts, at)}
          <div id="advbody"></div>
          <div class="techacts" id="advacts"></div>
          <div class="msg-slot" id="advmsg"></div>
        </div>
      </div>`);

    const host = document.getElementById("advbody")!;
    const acts = document.getElementById("advacts")!;
    const say = (html: string) => {
      const m = document.getElementById("advmsg");
      if (m) m.innerHTML = html;
    };
    const back = i > 0
      ? `<button class="btn ghost" id="aback">Back</button>` : "";
    const wireBack = () => document.getElementById("aback")
      ?.addEventListener("click", () => void go(ADVISOR_STEPS[i - 1]!.key));

    /* A step that cannot be done yet says so instead of pretending. */
    const why = step.blocked?.(facts);
    if (why) {
      host.innerHTML = `<div class="techstop"><b>Not yet.</b><p>${esc(why)}</p></div>`;
      acts.innerHTML = back + `<button class="btn primary" id="anext">What is next then</button>`;
      wireBack();
      document.getElementById("anext")?.addEventListener("click", () => void onward());
      return;
    }

    switch (step.key) {
      case "who": {
        const missing = [
          !facts.hasCustomer && "their name",
          !facts.hasPhone && "a mobile number",
          !facts.hasPlace && "where the van is going",
          !facts.hasCar && `what the ${shopWords().thing} is`,
          !facts.hasComplaint && "what it is doing",
        ].filter(Boolean) as string[];
        host.innerHTML = missing.length
          ? `<div class="techstop"><b>Not on the job yet</b><p>${esc(missing.join(", "))}.</p></div>
             <p class="techfine">None of this stops you. It is what a price is
             built from, and anything missing here is what gets argued about
             later &mdash; so it is worth two minutes now.</p>`
          : `<div class="techok"><b>Everything a quote needs is here.</b>
               <p>Name, number, where it is, what it is, and what it is doing.</p></div>`;
        acts.innerHTML = back
          + `<a class="btn" href="#/job/${esc(job.id)}">Fill it in on the job</a>`
          + `<button class="btn primary" id="anext">${missing.length ? "Carry on" : "Next"}</button>`;
        wireBack();
        document.getElementById("anext")?.addEventListener("click", () =>
          void go(ADVISOR_STEPS[i + 1]!.key));
        break;
      }

      case "price": {
        host.innerHTML = facts.visitQuote && !(facts.priced > 0)
          ? `<div class="techok"><b>A free estimate visit.</b>
               <p>No price on this one by design. The technician prices it at the
               door; this quote asks whether they want us to come out.</p></div>`
          : facts.priced > 0
          ? `<div class="techok"><b>${esc(money(facts.priced))} on the job.</b>
               <p>${lines.length} line${lines.length === 1 ? "" : "s"}. Change it on
               the job if the number is not right yet.</p></div>`
          : `<div class="techstop"><b>Nothing is priced.</b>
               <p>Add a line with money on it, or have it drafted from what the
               customer already told you.</p></div>`;
        acts.innerHTML = back
          + `<a class="btn" href="#/job/${esc(job.id)}">Open the line editor</a>`
          + `<button class="btn primary" id="anext">Next</button>`;
        wireBack();
        document.getElementById("anext")?.addEventListener("click", () => void onward());
        break;
      }

      /**
       * IT SENDS. It does not point at where sending happens.
       *
       * Anthony: "send quote is really far down and hard to find, I hope
       * this goes in service advisor wizard." It was three quarters of the
       * way down the job card, under the lines and past the money band --
       * the button the desk presses most and the one you had to scroll to.
       * Here it is the whole step, and it is the same code the job card
       * runs. See data/sendquote.ts.
       */
      case "send": {
        /* NO NUMBER IS NOT THE SAME AS NOWHERE TO SEND IT. This step used
           to stop dead on a missing mobile and send the advisor off to add
           one, mid-walk, with the customer standing there. An email address
           carries the same quote and the same approve link, so the walk
           only stops when there is genuinely neither. */
        const byEmail = !facts.hasPhone && canEmailQuote(job);
        const nowhere = !facts.hasPhone && !byEmail;
        host.innerHTML = (facts.quoteSent
          ? `<div class="techok"><b>Already sent.</b>
               <p>They have the price and a link to their own page. Sending
               again is fine if nobody answered.</p></div>`
          : `<div class="techsums"><div><span>Going to them</span>
               <b>${facts.visitQuote && !(facts.priced > 0)
                 ? "A free estimate visit" : esc(money(facts.priced))}</b></div></div>`)
          + (nowhere
            ? `<div class="techstop"><b>No mobile number and no email.</b>
                 <p>The quote needs somewhere to go. Add one on the job first.</p></div>`
            : `<p class="techfine">${esc(job.customers?.full_name ?? "They")}
               gets the figure and a link to their own page, where they can
               read what is on it and approve it. That starts a thirty day
               clock on the price.${byEmail
                 ? ` No mobile number on file, so this one goes by email to
                   ${esc(job.customers?.email ?? "")}.` : ""}</p>`);
        acts.innerHTML = back
          + (nowhere
            ? `<a class="btn primary" href="#/job/${esc(job.id)}">Add their details</a>`
            /* `glow` is on trial here and on no other button in the app.
               The quote going out is the one moment in the wizard worth
               pointing at, and one button is enough to judge it by. */
            : `<button class="btn primary glow" id="asend">${
                facts.quoteSent
                  ? (byEmail ? "Email it again" : "Send it again")
                  : (byEmail ? "Email it to them" : "Send it to them")}</button>`)
          + `<a class="btn ghost" href="#/job/${esc(job.id)}">Look at the lines first</a>`
          + `<button class="btn" id="anext">${facts.quoteSent ? "Next" : "Skip"}</button>`;
        wireBack();
        document.getElementById("anext")?.addEventListener("click", () => void onward());
        document.getElementById("asend")?.addEventListener("click", async () => {
          const b = document.getElementById("asend") as HTMLButtonElement;
          /* Put back what it said, not a fixed string -- this button reads
             "Email it to them" when there is no number, and a failed send
             must not rename it into a promise the card cannot keep. */
          const was = b.textContent ?? "Send it to them";
          b.disabled = true; b.textContent = "Sending\u2026";
          const r = await sendTheQuote(job, lines, facts.priced, () => confirm(
            "This quote already went to them in the last ten minutes. Send it again?"),
            byEmail ? "email" : "sms");
          if (!r.ok) {
            b.disabled = false; b.textContent = was;
            if (r.error) say(`<p class="msg bad">${esc(r.error)}</p>`);
            return;
          }
          say(`<p class="msg ok">${byEmail
            ? `Emailed to ${esc(job.customers?.email ?? "them")}. They can approve it on their own screen.`
            : "Sent. They can approve it on their own screen."}</p>`);
          await onward();
        });
        break;
      }

      case "yes": {
        if (facts.authorized) {
          host.innerHTML = `<div class="techok"><b>They said yes.</b>
            <p>It is on the job with who agreed, to what, and when.</p></div>`;
          acts.innerHTML = back + `<button class="btn primary" id="anext">Next</button>`;
        } else {
          host.innerHTML = `<p class="techfine">Signed on their own screen, or
            told to you on the phone. Either counts. Both go on the record.</p>
            <div id="advauth"></div>`;
          authForm("advauth", job.id, facts.priced,
            job.customers?.full_name ?? "", () => void onward(), [],
            Boolean(facts.visitQuote) && !(facts.priced > 0));
          acts.innerHTML = back + `<button class="btn ghost" id="anext">Skip for now</button>`;
        }
        wireBack();
        document.getElementById("anext")?.addEventListener("click", () => void onward());
        break;
      }

      case "book": {
        const local = shopWallValue(job.scheduled_at);
        host.innerHTML = `
          ${facts.scheduled ? `<div class="techok"><b>Booked.</b>
            <p>${esc(shopStamp(job.scheduled_at))}</p></div>` : ""}
          <label class="techfield"><span>Day and time</span>
            <input id="awhen" type="datetime-local" value="${esc(local)}"></label>`;
        acts.innerHTML = back + `<button class="btn primary" id="asave">${
          facts.scheduled ? "Change it" : "Book it"}</button>`;
        wireBack();
        document.getElementById("asave")?.addEventListener("click", async () => {
          const v = (document.getElementById("awhen") as HTMLInputElement).value;
          if (!v) { say(`<p class="msg bad">Pick a day and a time.</p>`); return; }
          const b = document.getElementById("asave") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Booking…";
          try { await setScheduledAt(job.id, v); await onward(); }
          catch (e) {
            b.disabled = false; b.textContent = "Book it";
            say(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
          }
        });
        break;
      }

      case "who_goes": {
        const crew = await jobTechnicians(job.id).catch(() => []);
        host.innerHTML = crew.length
          ? `<div class="techok"><b>Somebody is on it.</b>
               <p>${crew.length} on the crew. Change who on the job itself.</p></div>`
          : `<div class="techstop"><b>Nobody is on it yet.</b>
               <p>A job with no name on it is a job nobody is expecting.</p></div>`;
        acts.innerHTML = back
          + (crew.length ? "" : `<button class="btn" id="ame">Put me on it</button>`)
          + `<a class="btn" href="#/job/${esc(job.id)}">Choose somebody</a>`
          + `<button class="btn primary" id="anext">Next</button>`;
        wireBack();
        document.getElementById("ame")?.addEventListener("click", async () => {
          const b = document.getElementById("ame") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Adding…";
          try {
            const mine = await meAsTechnician();
            const now = await jobTechnicians(job.id).catch(() => []);
            const ids = [...new Set([...now.map((t) => t.technician_id), mine])];
            await setJobTechnicians(job.id, ids, now.find((t) => t.is_lead)?.technician_id ?? mine);
            await onward();
          } catch (e) {
            b.disabled = false; b.textContent = "Put me on it";
            say(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
          }
        });
        document.getElementById("anext")?.addEventListener("click", () => void onward());
        break;
      }

      case "bill": {
        const m = await moneyFor(job.id, facts.priced).catch(() => null);
        const raised = Boolean(m?.invoiceId);
        host.innerHTML = raised
          ? `<div class="techok"><b>Already billed &mdash; ${esc(money(Number(m?.total ?? 0)))}</b>
               <p>The invoice exists. Next is the money.</p></div>`
          : `<div class="techsums"><div><span>The work</span>
               <b>${esc(money(facts.priced))}</b></div></div>
             <p class="techfine">Raising the invoice sends nothing to the
             customer. It is the bill existing, so it can be paid.</p>`;
        acts.innerHTML = back + `<button class="btn primary" id="anext">${
          raised ? "Next" : "Raise the invoice"}</button>`;
        wireBack();
        document.getElementById("anext")?.addEventListener("click", async () => {
          if (raised) { await onward(); return; }
          const b = document.getElementById("anext") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Raising…";
          try { await raiseInvoice(job.id); await onward(); }
          catch (e) {
            b.disabled = false; b.textContent = "Raise the invoice";
            say(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
          }
        });
        break;
      }

      case "money": {
        const m = await moneyFor(job.id, facts.priced).catch(() => null);
        const owed = Math.max(0, Number(m?.total ?? 0) - Number(m?.paid ?? 0));
        if (owed <= 0.005) {
          host.innerHTML = `<div class="techok"><b>Settled.</b>
            <p>Nothing outstanding on this one.</p></div>`;
          acts.innerHTML = `<a class="btn primary wide" href="#/jobs">Done</a>`;
          return;
        }
        host.innerHTML = `
          <div class="techsums"><div><span>Outstanding</span>
            <b>${esc(money(owed))}</b></div></div>
          <label class="techfield"><span>How much</span>
            <input id="aamt" inputmode="decimal" value="${owed.toFixed(2)}"></label>
          <label class="techfield"><span>How they paid</span>
            <select id="amethod">${(Object.keys(METHOD_LABEL) as Method[])
              .map((k) => `<option value="${esc(k)}">${esc(METHOD_LABEL[k])}</option>`)
              .join("")}</select></label>
          <label class="techfield"><span>Reference, if there is one</span>
            <input id="aref" autocomplete="off"></label>`;
        acts.innerHTML = back + `<button class="btn primary" id="asave">Record it</button>`;
        wireBack();
        document.getElementById("asave")?.addEventListener("click", async () => {
          const amt = Number((document.getElementById("aamt") as HTMLInputElement).value);
          if (!(amt > 0)) { say(`<p class="msg bad">How much did they pay?</p>`); return; }
          const b = document.getElementById("asave") as HTMLButtonElement;
          b.disabled = true; b.textContent = "Recording…";
          try {
            await recordPayment(job.id, amt,
              (document.getElementById("amethod") as HTMLSelectElement).value as Method,
              (document.getElementById("aref") as HTMLInputElement).value);
            await onward();
          } catch (e) {
            b.disabled = false; b.textContent = "Record it";
            say(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
          }
        });
        break;
      }
    }
  };

  await draw();
}

/** What the entry card needs, without loading the walk. */
export { leftOn, nextOn, type Step };
